import IconButton from "./IconButton";
import {
  AssignmentTurnedInRounded as ReviewIcon,
  Category as ScenariosIcon,
  CloseRounded as CloseIcon,
  HowToVote as DealsIcon,
  ImportContacts as ReportsIcon,
  AutoStoriesRounded as ReportBuilderIcon,
  TableChartRounded as TableIcon,
  VpnKeyRounded as TokenIcon,
  BallotRounded as FormsIcon,
  ArticleRounded as TemplatesIcon,
  MenuRounded as SidebarIcon,
  Security as AdminIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { toSentenceCase } from "common/strings";
import Logo from "components/common/Logo";
import TriageRoutingIcon from "components/common/TriageRoutingIcon";
import { LEGACY_THEME } from "flags";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";
import * as userSelectors from "store/selectors/user/userSelectors";

const AppMenu = ({
  isUserReadOnly,
  isSuperUser,
  isAdminUser,
  componentVisibility,
  onChange,
}) => {
  const { isOn: legacyTheme, isLoading } = useFeatureFlag(LEGACY_THEME, {
    default: true,
  });
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const contexts = [
    {
      text: "Submissions",
      path: "/submissions",
      icon: <DealsIcon />,
      visible: true,
    },
    {
      text: "Reports",
      path: "/reports",
      icon: <ReportsIcon />,
      visible: isUserReadOnly ? false : null,
    },
    {
      text: "Report Builder",
      path: "/report-builder",
      icon: <ReportBuilderIcon />,
      visible: isUserReadOnly ? false : null,
    },
    {
      text: "CAUSE",
      path: "/programs/cause",
      icon: <TableIcon />,
    },
    {
      text: "Review",
      path: "/review",
      icon: <ReviewIcon />,
      visible: true,
    },
    {
      text: "Scenarios",
      path: "/scenarios",
      icon: <ScenariosIcon />,
      visible: isUserReadOnly ? false : null,
    },
    {
      text: "Token",
      path: "/token",
      icon: <TokenIcon />,
    },
    {
      text: "Forms",
      path: "/forms",
      icon: <FormsIcon />,
    },
    {
      text: "Triage Pipeline",
      path: "/triage-pipeline",
      icon: <TriageRoutingIcon fontSize={"small"} />,
    },
    {
      text: "Templates",
      path: "/templates",
      icon: <TemplatesIcon />,
      // Admin-only, and rolled out per company via the component-visibility
      // toggle (componentVisibility["<AppMenu>/templates"]).
      visible: isAdminUser ? undefined : false,
    },
    {
      text: "Settings",
      path: "/settings",
      icon: <SettingsIcon />,
      visible: isAdminUser,
    },
    {
      text: "Admin",
      path: "/admin",
      icon: <AdminIcon />,
      visible: isSuperUser,
    },
  ].filter(
    (x) => x.visible ?? componentVisibility[`<AppMenu>${x.path}`] ?? false
  );

  const location = useLocation();

  useEffect(() => {
    if (typeof onChange !== "function") return;
    const basePath = (location.pathname.substring(1) || "submissions").split(
      "/"
    )[0];
    onChange(toSentenceCase(basePath, "-"));
  }, [location, onChange]);

  return (
    <>
      <IconButton
        icon={SidebarIcon}
        className={"link"}
        onClick={toggleMenu}
        aria-label={"Open menu"}
        data-testid={"side-bar-button"}
      />
      <Drawer
        variant={"temporary"}
        anchor={"left"}
        open={isOpen}
        onClose={toggleMenu}
      >
        <Stack minWidth={240}>
          <List>
            <ListItem key={"APP_MENU_HEADER"}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                style={{ width: "100%" }}
              >
                {isLoading ? (
                  <Skeleton height={30} width={"60%"} />
                ) : (
                  <Stack alignItems={"flex-start"}>
                    <Logo height={legacyTheme ? 20 : 24} variant={"wordmark"} />
                    {!legacyTheme && (
                      <Typography variant={"subtitle2"}>
                        {"Risk Studio"}
                      </Typography>
                    )}
                  </Stack>
                )}
                <IconButton
                  icon={CloseIcon}
                  aria-label={"Close App Menu"}
                  edge={"end"}
                  onClick={toggleMenu}
                />
              </Stack>
            </ListItem>
            {contexts.map((context) => {
              return (
                <ListItemButton
                  component={Link}
                  to={context.path}
                  key={context.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`side-bar-content-${context.text}`}
                >
                  <ListItemIcon>{context.icon}</ListItemIcon>
                  <ListItemText primary={context.text} />
                </ListItemButton>
              );
            })}
          </List>
        </Stack>
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => ({
  isUserReadOnly: userSelectors.isUserReadOnly(state),
  isSuperUser: userSelectors.isSuperUser(state),
  isAdminUser: userSelectors.isAdminUser(state),
  componentVisibility:
    userSelectors.selectUserConfig(state).componentVisibility ?? {},
});

export default connect(mapStateToProps)(AppMenu);
